import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import adminApi from '../../services/adminApi';
import toast from 'react-hot-toast';

interface ContentPageDto {
  id: string;
  pageType: string;
  title: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateContentPageDto {
  pageType: string;
  title: string;
  content: string;
  isActive: boolean;
}

interface UpdateContentPageDto {
  title?: string;
  content?: string;
  isActive?: boolean;
}

const AdminContentPagesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<ContentPageDto | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingPage, setDeletingPage] = useState<ContentPageDto | null>(null);

  const pageTypes = [
    { value: 'TermsOfUse', label: 'شرایط استفاده' },
    { value: 'FAQ', label: 'سوالات متداول' },
    { value: 'PrivacyPolicy', label: 'سیاست حفظ حریم خصوصی' },
    { value: 'Rules', label: 'قوانین' }
  ];

  // Fetch content pages
  const { data: contentPages, isLoading, error } = useQuery({
    queryKey: ['admin-content-pages'],
    queryFn: async () => {
      const response = await adminApi.getContentPages();
      return response.data;
    },
  });

  // Create content page mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateContentPageDto) => adminApi.createContentPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-content-pages'] });
      setIsCreateModalOpen(false);
      toast.success('صفحه محتوا با موفقیت ایجاد شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در ایجاد صفحه محتوا');
    },
  });

  // Update content page mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContentPageDto }) => 
      adminApi.updateContentPage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-content-pages'] });
      setIsEditModalOpen(false);
      setEditingPage(null);
      toast.success('صفحه محتوا با موفقیت به‌روزرسانی شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی صفحه محتوا');
    },
  });

  // Delete content page mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteContentPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-content-pages'] });
      setIsDeleteModalOpen(false);
      setDeletingPage(null);
      toast.success('صفحه محتوا با موفقیت حذف شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در حذف صفحه محتوا');
    },
  });

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getPageTypeLabel = (pageType: string): string => {
    const pageTypeObj = pageTypes.find(pt => pt.value === pageType);
    return pageTypeObj?.label || pageType;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" dir="rtl">
        <p className="text-red-600">خطا در بارگذاری صفحات محتوا</p>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">مدیریت صفحات محتوا</h1>
            <p className="text-gray-600 mt-2">مدیریت صفحات محتوای پویا (شرایط استفاده، سوالات متداول، قوانین و غیره)</p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition duration-200 flex items-center gap-2 gap-2-reverse"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            افزودن صفحه جدید
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">{contentPages?.length || 0}</h3>
              <p className="text-gray-600">کل صفحات</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contentPages?.filter((page: ContentPageDto) => page.isActive).length || 0}
              </h3>
              <p className="text-gray-600">صفحات فعال</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contentPages?.filter((page: ContentPageDto) => !page.isActive).length || 0}
              </h3>
              <p className="text-gray-600">صفحات غیرفعال</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Pages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">لیست صفحات محتوا</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نوع صفحه</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عنوان</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">به‌روزرسانی</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contentPages?.map((page: ContentPageDto) => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getPageTypeLabel(page.pageType)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{page.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      page.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {page.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(page.updatedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <button
                        onClick={() => {
                          setEditingPage(page);
                          setIsEditModalOpen(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                      >
                        ویرایش
                      </button>
                      <button
                        onClick={() => {
                          setDeletingPage(page);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">ایجاد صفحه محتوا</h2>
            </div>
            <div className="p-6">
              <CreateContentPageForm
                pageTypes={pageTypes}
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setIsCreateModalOpen(false)}
                isSubmitting={createMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">ویرایش صفحه محتوا</h2>
            </div>
            <div className="p-6">
              <EditContentPageForm
                page={editingPage}
                onSubmit={(data) => updateMutation.mutate({ id: editingPage.id, data })}
                onCancel={() => {
                  setIsEditModalOpen(false);
                  setEditingPage(null);
                }}
                isSubmitting={updateMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">حذف صفحه محتوا</h2>
              <p className="text-gray-600 mb-6">
                آیا مطمئن هستید که می‌خواهید "{deletingPage.title}" را حذف کنید؟ این عمل قابل بازگشت نیست.
              </p>
              <div className="flex justify-end space-x-4 space-x-reverse">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeletingPage(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  لغو
                </button>
                <button
                  onClick={() => deleteMutation.mutate(deletingPage.id)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'در حال حذف...' : 'حذف'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Form Component
interface CreateContentPageFormProps {
  pageTypes: Array<{ value: string; label: string }>;
  onSubmit: (data: CreateContentPageDto) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CreateContentPageForm: React.FC<CreateContentPageFormProps> = ({
  pageTypes,
  onSubmit,
  onCancel,
  isSubmitting
}) => {
  const [formData, setFormData] = useState<CreateContentPageDto>({
    pageType: '',
    title: '',
    content: '',
    isActive: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">نوع صفحه</label>
        <select
          value={formData.pageType}
          onChange={(e) => setFormData({ ...formData, pageType: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        >
          <option value="">انتخاب نوع صفحه</option>
          {pageTypes.map((pt) => (
            <option key={pt.value} value={pt.value}>{pt.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">عنوان</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">محتوا (HTML/React مجاز است)</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={15}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
          required
        />
        <p className="mt-2 text-sm text-gray-500">می‌توانید از کد HTML یا React JSX استفاده کنید. محتوا به صورت پویا رندر می‌شود.</p>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="isActive" className="mr-2 text-sm font-medium text-gray-700">فعال</label>
      </div>

      <div className="flex justify-end space-x-4 space-x-reverse pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
        >
          لغو
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50"
        >
          {isSubmitting ? 'در حال ایجاد...' : 'ایجاد صفحه'}
        </button>
      </div>
    </form>
  );
};

// Edit Form Component
interface EditContentPageFormProps {
  page: ContentPageDto;
  onSubmit: (data: UpdateContentPageDto) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const EditContentPageForm: React.FC<EditContentPageFormProps> = ({
  page,
  onSubmit,
  onCancel,
  isSubmitting
}) => {
  const [formData, setFormData] = useState<UpdateContentPageDto>({
    title: page.title,
    content: page.content,
    isActive: page.isActive
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">نوع صفحه</label>
        <input
          type="text"
          value={page.pageType}
          disabled
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">عنوان</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">محتوا (HTML/React مجاز است)</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={15}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
          required
        />
        <p className="mt-2 text-sm text-gray-500">می‌توانید از کد HTML یا React JSX استفاده کنید. محتوا به صورت پویا رندر می‌شود.</p>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="isActive" className="mr-2 text-sm font-medium text-gray-700">فعال</label>
      </div>

      <div className="flex justify-end space-x-4 space-x-reverse pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
        >
          لغو
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50"
        >
          {isSubmitting ? 'در حال به‌روزرسانی...' : 'به‌روزرسانی صفحه'}
        </button>
      </div>
    </form>
  );
};

export default AdminContentPagesPage;

